import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { keyBy, uniq } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  GetPeopleTableMetadataQueryQuery,
  GetPeopleTableMetadataQueryQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import Table, {
  TableBody,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { assertEdgesNonNull } from "@helpers/helpers";

import getPeopleTableMetadataQuery from "../graphql/get-people-table-metadata-query";
import DashboardPeopleTableRow from "./people-table-row";
import DashboardTeamSettingsAddButton from "./team-settings-add-button";

export const peopleTableGoalMax = 5;

const PeopleTableHead = ({
  isShowingOneonone = false,
}: {
  isShowingOneonone?: boolean;
}) => {
  const currentOrganization = currentOrganizationVar();
  const label = useLabel();
  return (
    <TableHeadRow>
      <TableHeadCell>Name</TableHeadCell>
      {currentOrganization.featureFlags.goals && (
        <TableHeadCell
          widthPx={120}
          cellClassName="hidden @lg/people-table:table-cell"
        >
          {label("goal", { pluralize: true, capitalize: true })}
        </TableHeadCell>
      )}
      <TableHeadCell widthPx={170}>
        Next{" "}
        {isShowingOneonone
          ? label("oneonone", { pluralize: false, capitalize: true })
          : "Meeting"}
      </TableHeadCell>
      {currentOrganization.featureFlags.recognitions && (
        <TableHeadCell
          widthPx={180}
          cellClassName="hidden @2xl/people-table:table-cell"
        >
          Last {label("recognition")}
        </TableHeadCell>
      )}
    </TableHeadRow>
  );
};

const _DashboardPeopleTable = () => {
  const [isShowingEditDialog, setIsShowingEditDialog] = useState(false);
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const { uiPreferenceCache } = useUiPreferenceCache();

  const managers = currentUser?.managers
    ? assertEdgesNonNull(currentUser.managers)
    : [];
  const reports = currentUser?.directReports
    ? assertEdgesNonNull(currentUser.directReports)
    : [];
  const peers = currentUser?.peers ? assertEdgesNonNull(currentUser.peers) : [];
  const favourites = currentUser?.favouritesGroups
    ? assertEdgesNonNull(currentUser.favouritesGroups)
    : [];
  const favouriteUsers = favourites.reduce((memo, favouriteGroup) => {
    const users = assertEdgesNonNull(favouriteGroup.users).map((user) => ({
      ...user,
    }));
    return [...memo, ...users];
  }, [] as (BasicUser & { __typename: "UserNode" })[]);

  const oneononeUserIds = uniq([
    ...reports.map((report) => report.id),
    ...managers.map((manager) => manager.id),
  ]);
  const meetingUserIds = uniq([
    ...peers.map((directTeamUser) => directTeamUser.id),
    ...favouriteUsers.map((favouriteUser) => favouriteUser.id),
  ]);
  const { data: metadataData, loading: metadataLoading } = useQuery<
    GetPeopleTableMetadataQueryQuery,
    GetPeopleTableMetadataQueryQueryVariables
  >(getPeopleTableMetadataQuery, {
    notifyOnNetworkStatusChange: true,
    skip: !uiPreferenceCache.dashboardConnectionsContainerExpanded,
    variables: {
      oneononeUserIds,
      meetingUserIds,
      hasOneononeUserIds: oneononeUserIds.length > 0,
      hasMeetingUserIds: meetingUserIds.length > 0,
      peopleTableGoalMax,
      currentUserId: currentUser.id,
      now: moment().startOf("hour").format(),
    },
  });
  const meetingUsersWithMetadata = metadataData?.meetingUsers
    ? assertEdgesNonNull(metadataData.meetingUsers)
    : [];
  const oneononeUsersWithMetadata = metadataData?.oneononeUsers
    ? assertEdgesNonNull(metadataData.oneononeUsers)
    : [];
  const allUsersWithMetadata = [
    ...oneononeUsersWithMetadata,
    ...meetingUsersWithMetadata,
  ].map((user) => ({
    ...user,
    meetings: user.meetings ? assertEdgesNonNull(user.meetings) : [],
  }));
  const metadatabyUserId = keyBy(allUsersWithMetadata, "id");

  const countClassName =
    "px-1.5 py-0.5 bg-gray-100 rounded-md font-semibold text-xs text-gray-900 flex items-center gap-0.5";

  // RENDER
  return (
    <>
      {isShowingEditDialog && (
        <DashboardTeamSettingsAddButton
          managers={managers}
          reports={reports}
          favouriteUsers={favouriteUsers}
          onClose={() => setIsShowingEditDialog(false)}
        />
      )}
      <Layout.MainSection
        title="My Connections"
        options={[
          {
            label: "Edit",
            onClick: ({ syntheticEvent }) => {
              syntheticEvent.preventDefault();
              syntheticEvent.stopPropagation();
              setIsShowingEditDialog(true);
            },
          },
        ]}
        expandedUiPreferenceKey="dashboardConnectionsContainerExpanded"
        className="@container/people-table"
        aria-label="People table container"
      >
        {reports.length === 0 &&
          managers.length === 0 &&
          favouriteUsers.length === 0 &&
          (!organization.featureFlags.homepageMyConnectionsPeers ||
            (organization.featureFlags.homepageMyConnectionsPeers &&
              peers.length === 0)) && (
            <div className="mt-6 text-gray-500">No connections found.</div>
          )}
        {reports.length > 0 && (
          <Layout.MainSubSection
            title={
              <div className="flex items-center gap-1.5">
                Reports<span className={countClassName}>{reports.length}</span>
              </div>
            }
          >
            <TableContainer>
              <Table className="table-fixed">
                <PeopleTableHead isShowingOneonone />
                <TableBody>
                  {reports.map((report) => (
                    <DashboardPeopleTableRow
                      selectedUser={currentUser}
                      userMetadata={metadatabyUserId[report.id]}
                      user={report}
                      key={report.id}
                      loadingArtifacts={metadataLoading}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Layout.MainSubSection>
        )}
        {managers.length > 0 && (
          <Layout.MainSubSection
            title={
              <div className="flex items-center gap-1.5">
                Managers
                <span className={countClassName}>{managers.length}</span>
              </div>
            }
          >
            <TableContainer>
              <Table className="table-fixed">
                <PeopleTableHead isShowingOneonone />
                <TableBody>
                  {managers.map((manager) => (
                    <DashboardPeopleTableRow
                      selectedUser={currentUser}
                      userMetadata={metadatabyUserId[manager.id]}
                      user={manager}
                      key={manager.id}
                      loadingArtifacts={metadataLoading}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Layout.MainSubSection>
        )}
        {organization.featureFlags.homepageMyConnectionsPeers &&
          peers.length > 0 && (
            <Layout.MainSubSection
              title={
                <div className="flex items-center gap-1.5">
                  Peers
                  <span className={countClassName}>{peers.length}</span>
                </div>
              }
            >
              <TableContainer>
                <Table className="table-fixed">
                  <PeopleTableHead />
                  <TableBody>
                    {peers.map((directTeamUser) => (
                      <DashboardPeopleTableRow
                        selectedUser={currentUser}
                        userMetadata={metadatabyUserId[directTeamUser.id]}
                        user={directTeamUser}
                        key={directTeamUser.id}
                        loadingArtifacts={metadataLoading}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Layout.MainSubSection>
          )}
        {favouriteUsers.length > 0 && (
          <Layout.MainSubSection
            title={
              <div className="flex items-center gap-1.5">
                Followers
                <span className={countClassName}>{favouriteUsers.length}</span>
              </div>
            }
          >
            <TableContainer>
              <Table className="table-fixed">
                <PeopleTableHead />
                <TableBody>
                  {favouriteUsers.map((favouriteUser) => (
                    <DashboardPeopleTableRow
                      selectedUser={currentUser}
                      userMetadata={metadatabyUserId[favouriteUser.id]}
                      user={favouriteUser}
                      key={favouriteUser.id}
                      loadingArtifacts={metadataLoading}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Layout.MainSubSection>
        )}
      </Layout.MainSection>
    </>
  );
};

const DashboardPeopleTable = withErrorBoundary(_DashboardPeopleTable, {
  fallback: <Error description={"The people table could not be rendered."} />,
});
export default DashboardPeopleTable;
